import { Inject, Injectable } from '@angular/core';

import { NgRedux } from '@angular-redux/store';
import Env from '@ridango/core/env';

import { Observable } from 'rxjs';

import { ApplicationFacade } from '../../../application';
import { MediumFacade } from '../../../medium';
import { PurchaseFacade } from '../../../purchase';
import { RelatedCardFacade } from '../../../related-card';
import { ShoppingCartFacade } from '../../../shopping-cart';
import { LoginActions } from './login.actions';
import { LoginCredentials } from './login.reducer';
import * as LoginSelectors from './login.selectors';
import { DeviceNotificationService } from '../../../firebase';
import { AccountTicketsNgrxFacade } from '../../../account-tickets-ngrx';

@Injectable()
export class LoginFacade {
    forceTermsAgreement$: Observable<boolean> = this.store.select(LoginSelectors.getForceTermsAgreement);
    forcePasswordReset$: Observable<boolean> = this.store.select(LoginSelectors.getForcePasswordReset);
    isAuthenticated$: Observable<boolean> = this.store.select(LoginSelectors.getIsAuthenticated);
    loading$: Observable<boolean> = this.store.select(LoginSelectors.getLoading);
    error$: Observable<boolean> = this.store.select(LoginSelectors.getError);
    termsModalVisible$: Observable<boolean> = this.store.select((state) => state.auth.login.termsModalVisible);
    authAccessToken$: Observable<string> = this.store.select(LoginSelectors.getAuthAccessToken);
    verificationCode$: Observable<string> = this.store.select(LoginSelectors.getVerificationCode);

    constructor(
        @Inject('ENV') public env: Env,
        private store: NgRedux<any>,
        private action: LoginActions,
        private applicationFacade: ApplicationFacade,
        private shoppingCartFacade: ShoppingCartFacade,
        private mediumFacade: MediumFacade,
        private relatedCardFacade: RelatedCardFacade,
        private purchaseFacade: PurchaseFacade,
        private firebaseService: DeviceNotificationService,
        private accountTicketsFacade: AccountTicketsNgrxFacade
    ) {}

    loginWithCredentials(credentials: LoginCredentials) {
        if (credentials.callback_url) {
            this.store.dispatch(this.action.loginAuthTokenOnly(credentials));
        } else {
            this.store.dispatch(this.action.login(credentials));
        }
    }

    loginWithIdCard(token: any, nonce: string) {
        this.store.dispatch(this.action.loginWithIdCard(token, nonce));
    }

    loginWithMobileId(verificationCode: string, hash: string) {
        this.store.dispatch(this.action.loginWithMobileId(verificationCode, hash));
    }

    loginWithSmartId(verificationCode: string, hash: string) {
        this.store.dispatch(this.action.loginWithSmartId(verificationCode, hash));
    }

    loginWithViisp(ticket: string) {
        this.store.dispatch(this.action.loginWithViisp(ticket));
    }

    loginWithOidcExchange(exchangeId: string) {
        this.store.dispatch(this.action.loginWithOidcExchange(exchangeId));
    }

    setAuthAccessToken(token: string) {
        this.store.dispatch(this.action.loginSuccessAuthTokenOnly(token));
    }

    loginAfterAgreement(ondemandRequest: boolean, token?: string) {
        let payload: any = {};
        payload = {
            terms: this.applicationFacade.getTerms(),
            reason: this.store.getState().auth.login.forceTermsReason
        };

        if (ondemandRequest) {
            payload.ondemandRequest = ondemandRequest;
        }

        if (token) {
            payload.token = token;
        }

        this.store.dispatch(this.action.loginAfterAgree(payload));
    }

    forceAgreement(reason: string) {
        this.store.dispatch(this.action.forceAgreement(reason));
    }

    resetForcedAgreement() {
        this.store.dispatch(this.action.resetForcedAgreement());
    }

    forcePasswordReset(payload: boolean) {
        this.store.dispatch(this.action.forcePasswordReset(payload));
    }

    loginCleanUp() {
        this.relatedCardFacade.resetRelatedCards();
        this.mediumFacade.clear();
        this.shoppingCartFacade.deleteCart();
        this.purchaseFacade.unsetShoppingCart();
        this.accountTicketsFacade.resetAccountTickets();
    }

    resetLoginState() {
        this.store.dispatch(this.action.loginReset());
    }

    logout(shouldRedirect: boolean = true) {
        this.loginCleanUp();
        this.store.dispatch(this.action.loginReset());
        this.store.dispatch(this.action.logout(shouldRedirect));
    }

    cancelChallengePolling() {
        this.store.dispatch(this.action.cancelChallengePolling());
    }

    showTermsModal() {
        this.store.dispatch(this.action.showTermsModal());
    }

    hideTermsModal() {
        this.store.dispatch(this.action.hideTermsModal());
    }
}
